import { useCallback, useEffect, useMemo } from 'react';
import { Element } from 'react-bulma-components';
import {
  matchPath,
  Outlet,
  ScrollRestoration,
  useLocation,
} from 'react-router-dom';
import { RouteObject } from '../../app';
import Footer from '../../ui/footer/footer';
import Navbar from '../../ui/navbar/navbar';
import ResolutionScalingWarningHeader from '../../ui/resolution-scaling-warning/resolution-scaling-warning-header/resolution-scaling-warning-header';
import ApiErrorElement from '../api-error-element/api-error-element';
import Conditional from '../conditional/conditional';
import useError from '../hooks/error';
import useUser from '../hooks/user';
import If from '../if/if';
import PermissionsErrorCard from '../permissions-error-card/permissions-error-card';
import styles from './layout.module.scss';

function RootLayout({ availableRoutes }: { availableRoutes: RouteObject[] }) {
  const { error, clearError } = useError();
  const { user } = useUser();
  const location = useLocation();

  const getActiveRoute = useCallback(
    (location: string, routes: RouteObject[]): RouteObject | null => {
      let activeRoute: RouteObject | null = null;

      for (const route of routes) {
        if (route.path) {
          const match = matchPath(route.path, location);
          if (match) {
            activeRoute = route;
            break;
          }

          const partialMatch = location.startsWith(`/${route.path}`);
          if (partialMatch) {
            if (route.children && route.children.length > 0) {
              const childLocation =
                location.split('/').length > 2
                  ? location.replace(/^\/[^/]+(\/.*)$/, '$1')
                  : location;

              const childActiveRoute = getActiveRoute(
                childLocation,
                route.children
              );
              if (childActiveRoute) {
                activeRoute = childActiveRoute;
                break;
              }
            }
          }
        } else {
          if (route.index && location === '/') {
            activeRoute = route;
            break;
          }

          if (route.children && route.children.length > 0) {
            const childActiveRoute = getActiveRoute(location, route.children);
            if (childActiveRoute) {
              activeRoute = childActiveRoute;
              break;
            }
          }
        }
      }

      return activeRoute;
    },
    []
  );

  const activeRoute = useMemo(() => {
    return getActiveRoute(location.pathname, availableRoutes);
  }, [availableRoutes, getActiveRoute, location.pathname]);

  useEffect(() => {
    clearError();
  }, [clearError, location.key]);

  return (
    <Element className={styles.app}>
      <ScrollRestoration />
      <If condition={!activeRoute?.isMobileFriendly}>
        <div className={styles.warning}>
          <ResolutionScalingWarningHeader />
        </div>
      </If>
      <Navbar />
      <Element className={styles.content}>
        <Conditional condition={!error}>
          <Conditional.True>
            <Conditional condition={!!user.activeRole}>
              <Conditional.True>
                <Outlet />
              </Conditional.True>
              <Conditional.False>
                <PermissionsErrorCard />
              </Conditional.False>
            </Conditional>
          </Conditional.True>
          <Conditional.False>
            <ApiErrorElement />
          </Conditional.False>
        </Conditional>
      </Element>
      <Footer />
    </Element>
  );
}

export default RootLayout;
